/**
 * Single source of truth for "which brand colour should this job actually show" — used by every
 * SRP hit card, the similar-jobs section, and both the V1 and V2 job detail pages.
 *
 * Before this existed, six call sites each duplicated `isTyomarkkinatori ? TYOMARKKINATORI_PURPLE
 * : brandColour` inline. `isTyomarkkinatori` describes the JOB's source (was this posting
 * ingested via the Työmarkkinatori feed), not whether the linked COMPANY lacks a real brand
 * colour — so that inline check unconditionally masked real per-company colours (e.g. staffing
 * agencies like Osuma Henkilöstöpalvelut `#f01415` or Hunaja Works Oy `#fcec21`, which post many
 * TMT-sourced jobs but have perfectly good brand colours) behind a generic purple, on every job
 * they posted through TMT. See CompanyLogo.tsx's `sources` ordering for the identical bug in the
 * logo picker, fixed the same way: prefer the real per-company value always, and only substitute
 * the generic TMT marker when the company truly has none of its own.
 */

/** Decorative colour shown for a TMT-sourced job when the company has no real brand colour of its own. */
export const TYOMARKKINATORI_BRAND_COLOUR = '#6B46C1';

/**
 * Sentinel written by the colour-extraction pipeline (`computeBrandColour.ts` / `seedAbout.ts`)
 * when no real dominant colour could be found in a company's logo — not a genuine black brand
 * colour. Matched case-insensitively; a real black brand mark (Supercell, Linear, Camunda, ...)
 * uses this exact value too, so this only ever affects TMT-sourced jobs (see
 * `resolveEffectiveBrandColour`) — non-TMT jobs pass a literal `#000000` straight through
 * unchanged, same as before this module existed.
 */
const UNSET_BRAND_COLOUR_SENTINEL = '#000000';

/**
 * True when `hex` is a real, usable per-company brand colour: present, and not the pipeline's
 * "couldn't find one" sentinel. Deliberately does NOT judge saturation/lightness/near-white here
 * (each renderer already has its own contrast/usability rules downstream, e.g.
 * `adjustBrandColor`'s achromatic check or `isWhiteOrNearWhite`) — this only answers "did the
 * company actually have a colour on file," not "is it a good one to paint on screen."
 */
export function hasRealBrandColour(hex: string | null | undefined): hex is string {
  if (!hex) return false;
  return hex.trim().toUpperCase() !== UNSET_BRAND_COLOUR_SENTINEL;
}

function coerceIsTyomarkkinatori(value: boolean | 'true' | 'false' | null | undefined): boolean {
  return value === true || value === 'true';
}

/**
 * Resolves the brand colour hex to actually display for a job.
 *
 * - A real per-company colour (see `hasRealBrandColour`) always wins, TMT-sourced or not.
 * - Only when the company has no real colour of its own AND the job is TMT-sourced, falls back
 *   to the shared `TYOMARKKINATORI_BRAND_COLOUR` decorative identity.
 * - Otherwise (no real colour, not TMT-sourced) returns `rawBrandColour` unchanged, preserving
 *   whatever each call site already did for that case (e.g. a downstream neutral-colour fallback).
 *
 * Generic over the input's exact null-ness so callers whose `brandColour` field is typed
 * `string | undefined` (never `null`) get that same shape back, instead of every caller having to
 * deal with a `null` their own type never actually allows.
 */
export function resolveEffectiveBrandColour<T extends string | null | undefined>(
  rawBrandColour: T,
  isTyomarkkinatori: boolean | 'true' | 'false' | null | undefined,
): T | string {
  if (hasRealBrandColour(rawBrandColour)) return rawBrandColour;
  return coerceIsTyomarkkinatori(isTyomarkkinatori) ? TYOMARKKINATORI_BRAND_COLOUR : rawBrandColour;
}
